import { FormLabel } from '@Components/Common/FormComponent';
import LoadingBtn from '@Components/Common/LoadingBtn';
import {
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Space,
  Stack,
  Text,
  Textarea,
} from '@tonic-one/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CorrelatedExpression,
  CorrelatedExpressionShallow,
  RuleType,
  ConditionOperator,
  isConditionIntFormat,
  isConditionStringFormat,
  ConditionLogicOperator,
  CorrelatedExpressionCondition,
  isConditionListStringFormat,
} from '../types';
import { useQuery } from '@tanstack/react-query';
import { useOrgStore } from '@Models/useOrgModel';
import { RiskAIApi } from '@Apis';
import { queryClient } from 'src/QueryClient';
import { useRiskAIConditionsQuery } from '../query';
import isNil from 'lodash/isNil';
import ExpressionContentEditor from './ExpressionContentEditor';
import { useSocialGraphEnabledQuery } from '../../SocialGraph/query';
import { filterSocialGraphConditions } from '../utils';

const getEmptyCondition = () => {
  return {
    id: '',
    name: '',
    operator: ConditionOperator.IntLTE,
    marker: '',
  } satisfies CorrelatedExpressionCondition;
};

const CustomExpressionModal = (props: {
  defaultExpression?: CorrelatedExpressionShallow;
  isOpen: boolean;
  onSave: (expression: CorrelatedExpression) => Promise<void>;
  onClose: () => void;
}) => {
  const { isOpen, onSave, onClose, defaultExpression } = props;
  const { t } = useTranslation();
  const { orgId } = useOrgStore();
  const [expressionId] = useState(defaultExpression?.raiExpId ?? '');
  const [expressionName, setExpressionName] = useState(
    defaultExpression?.raiExpName ?? ''
  );
  const [expressionDesc, setExpressionDesc] = useState(
    defaultExpression?.raiExpDesc ?? ''
  );
  const isEmailLoop = defaultExpression?.raiExpMarkers === 'email_loop';
  const [selectedConditions, setSelectedConditions] = useState<
    CorrelatedExpressionCondition[]
  >([getEmptyCondition()]);
  const [conditionLogic] = useState<ConditionLogicOperator>(
    ConditionLogicOperator.AND
  );

  const { data: socialGraphEnabled, isLoading: isLoadingSocialGraph } =
    useSocialGraphEnabledQuery();

  const { data: _allConditions, isLoading: isLoadingAllCondition } =
    useRiskAIConditionsQuery();

  const allConditions = useMemo(() => {
    if (!_allConditions) return undefined;
    return filterSocialGraphConditions(
      _allConditions ?? [],
      socialGraphEnabled ?? false
    );
  }, [_allConditions, socialGraphEnabled]);

  const { data: defaultExpressionContent, isLoading: isLoadingExpContent } =
    useQuery({
      enabled: !!defaultExpression,
      queryKey: ['correlatedExpression', orgId, defaultExpression?.raiExpId],
      queryFn: async () => {
        return await RiskAIApi.getExpression(
          orgId,
          (defaultExpression as CorrelatedExpressionShallow).raiExpId
        );
      },
      select: (res) => res?.data?.raiExpContent,
      meta: {
        errorNotification: {
          title: t('common.apiException.internalError'),
        },
      },
    });

  useEffect(() => {
    if (defaultExpressionContent) {
      setSelectedConditions(defaultExpressionContent.conditions);
    }
  }, [defaultExpressionContent]);

  const isLoading =
    isLoadingAllCondition || isLoadingExpContent || isLoadingSocialGraph;

  const validExpressionDefinition = useCallback(
    (conditions: CorrelatedExpressionCondition[]) => {
      let error: string | null = null;
      for (const condition of conditions) {
        const conditionDefinition = (allConditions ?? []).find(
          (c) => c.raiCondId === condition.id
        );
        if (!conditionDefinition) {
          error = t('common.apiException.internalError');
          return error;
        }
        const { raiCondFormat, raiCondName, raiCondDataType } =
          conditionDefinition;
        // Verify that selected values are valid according to format
        if (
          isConditionIntFormat(raiCondFormat, raiCondDataType) &&
          condition.value
        ) {
          if (condition.value < raiCondFormat.minValue) {
            error = t('globalSetting.correlatedExpressions.error.minValue', {
              name: raiCondName,
              limit: raiCondFormat.minValue,
            });
            return error;
          }
          if (condition.value > raiCondFormat.maxValue) {
            error = t('globalSetting.correlatedExpressions.error.maxValue', {
              name: raiCondName,
              limit: raiCondFormat.maxValue,
            });
            return error;
          }
        } else if (
          isConditionStringFormat(raiCondFormat, raiCondDataType) &&
          condition.valueString
        ) {
          if (condition.valueString.length < raiCondFormat.minLength) {
            error = t('globalSetting.correlatedExpressions.error.minLength', {
              name: raiCondName,
              limit: raiCondFormat.minLength,
            });
            return error;
          } else if (condition.valueString.length > raiCondFormat.maxLength) {
            error = t('globalSetting.correlatedExpressions.error.maxLength', {
              name: raiCondName,
              limit: raiCondFormat.maxLength,
            });
            return error;
          }
        } else if (
          isConditionListStringFormat(raiCondFormat, raiCondDataType) &&
          Array.isArray(condition.valueStrings)
        ) {
          if (condition.valueStrings.length > raiCondFormat.maxEntries) {
            error = t('globalSetting.correlatedExpressions.error.maxEntries', {
              name: raiCondName,
              limit: raiCondFormat.maxEntries,
            });
            return error;
          }
          condition.valueStrings.forEach((value) => {
            if (value.length < raiCondFormat.eachEntryMinLength) {
              error = t(
                'globalSetting.correlatedExpressions.error.eachEntryMinLength',
                {
                  name: raiCondName,
                  limit: raiCondFormat.eachEntryMinLength,
                }
              );
              return error;
            } else if (value.length > raiCondFormat.eachEntryMaxLength) {
              error = t(
                'globalSetting.correlatedExpressions.error.eachEntryMaxLength',
                {
                  name: raiCondName,
                  limit: raiCondFormat.eachEntryMaxLength,
                }
              );
              return error;
            }
          });
        }
      }
      return error;
    },
    [allConditions, t]
  );

  const validExpressionName = useCallback(() => {
    let error: string | null = null;
    const validName = expressionName !== '' && expressionName.length <= 256;
    if (!validName) {
      error = t('globalSetting.correlatedRules.error.maxNameLength', {
        limit: 256,
      });
    }
    return error;
  }, [expressionName, t]);

  const validExpressionDesc = useCallback(() => {
    let error: string | null = null;
    const validDescription = expressionDesc.length <= 512;
    if (!validDescription) {
      error = t('globalSetting.correlatedRules.error.maxDescLength', {
        limit: 512,
      });
    }
    return error;
  }, [expressionDesc.length, t]);

  const requiredFieldsAreFilled = useMemo(() => {
    return (
      selectedConditions.length > 0 &&
      selectedConditions.every(({ id }) => !isNil(id)) &&
      expressionName !== ''
    );
  }, [selectedConditions, expressionName]);

  const handleSubmit = useCallback(async () => {
    // trim all the string fields
    const newExpression: CorrelatedExpression = {
      raiExpId: expressionId,
      raiExpBuiltIn: RuleType.UserDefined,
      raiExpName: expressionName.trim(),
      raiExpDesc: expressionDesc.trim(),
      raiExpContent: {
        conditions: selectedConditions.map((condition) => {
          // trim all the string fields
          if (condition.valueString) {
            condition.valueString = condition.valueString?.trim();
          }
          if (condition.valueStrings) {
            condition.valueStrings = condition.valueStrings.map((value) =>
              value.trim()
            );
            if (
              condition.valueStrings[condition.valueStrings.length - 1] === ''
            ) {
              condition.valueStrings.pop();
            }
          }
          return condition;
        }),
        conditionLogic,
      },
    };
    const expressionNameError = validExpressionName();
    const expressionDescError = validExpressionDesc();
    const expressionDefinitionError = validExpressionDefinition(
      newExpression.raiExpContent.conditions
    );
    const isValid =
      !expressionNameError &&
      !expressionDescError &&
      !expressionDefinitionError &&
      requiredFieldsAreFilled;
    if (isValid) {
      await onSave(newExpression);
      if (expressionId !== '') {
        queryClient.invalidateQueries({
          queryKey: ['correlatedExpression', orgId, expressionId],
        });
      }
    } else {
      if (expressionNameError) {
        window.showNotification({
          titleMsg: expressionNameError,
          severity: 'error',
        });
      }
      if (expressionDescError) {
        window.showNotification({
          titleMsg: expressionDescError,
          severity: 'error',
        });
      }
      if (expressionDefinitionError) {
        window.showNotification({
          titleMsg: expressionDefinitionError,
          severity: 'error',
        });
      }
    }
  }, [
    expressionId,
    expressionName,
    expressionDesc,
    selectedConditions,
    conditionLogic,
    validExpressionName,
    validExpressionDesc,
    validExpressionDefinition,
    requiredFieldsAreFilled,
    onSave,
    orgId,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnEsc isClosable>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          {t('globalSetting.correlatedExpressions.userDefinedModal.title')}
        </ModalHeader>
        <ModalBody>
          <Stack gap="6x">
            <Stack gap="4x">
              <Text size="lg">
                {t(
                  'globalSetting.correlatedRules.preDefinedModal.basicProperties'
                )}
              </Text>
              <Flex alignItems="center">
                <FormLabel
                  label={t(
                    'globalSetting.correlatedRules.preDefinedModal.name'
                  )}
                  required
                />
                {isEmailLoop ? (
                  <Text>{expressionName}</Text>
                ) : (
                  <Input
                    value={expressionName}
                    onChange={(e) => setExpressionName(e.target.value)}
                  />
                )}
              </Flex>
              <Flex>
                <FormLabel
                  label={t(
                    'globalSetting.correlatedRules.preDefinedModal.description'
                  )}
                />
                {isEmailLoop ? (
                  <Text dangerouslySetInnerHTML={{ __html: expressionDesc }} />
                ) : (
                  <Textarea
                    value={expressionDesc}
                    onChange={(e) => setExpressionDesc(e.target.value)}
                  />
                )}
              </Flex>
            </Stack>
            <Stack minHeight={300} gap="4x">
              <Box>
                <Text size="lg">
                  {t(
                    'globalSetting.correlatedExpressions.userDefinedModal.signalDefinition'
                  )}
                </Text>
                {/* <Text mt="1x" color={colorStyle.color.secondary}>
                  {t(
                    'globalSetting.correlatedExpressions.userDefinedModal.signalDefinitionDesc'
                  )}
                </Text> */}
              </Box>
              {isLoading ? (
                <Skeleton width="100%" height="60px" animation="pulse" />
              ) : (
                <ExpressionContentEditor
                  allConditions={allConditions ?? []}
                  selectedConditions={selectedConditions}
                  conditionLogic={conditionLogic}
                  onChange={setSelectedConditions}
                  isEmailLoop={isEmailLoop}
                />
              )}
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <LoadingBtn
            variant="primary"
            disabled={!requiredFieldsAreFilled}
            onClick={handleSubmit}
          >
            {t('common.action.label.save')}
          </LoadingBtn>
          <Space width="2x" />
          <Button onClick={onClose}>{t('common.button.cancel')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CustomExpressionModal;
